import { courseHandicap, groupRelativePlayingHandicap, netScoresFromGross, playingHandicap } from '@/lib/games/handicap';
import { computeNassau, computeSkins, type SkinsPlayer } from '@/lib/games/scoring';
import type { CourseHole, GameParticipant, MemberPublic } from '@/lib/db/types';

export interface HoleScores {
  [participantKey: string]: (number | null)[];
}

export function buildPlayers(
  participants: GameParticipant[],
  scores: HoleScores,
  memberMap: Record<string, MemberPublic>,
  courseHoles: CourseHole[],
  formatId: string,
  scoring: 'net' | 'gross',
  handicapAllowance?: number | null,
): SkinsPlayer[] {
  const strokeIndexes = courseHoles.sort((a, b) => a.hole_num - b.hole_num).map((h) => h.stroke_index);
  const courseHcps = participants.map((p) => {
    const hcp = p.member_id
      ? memberMap[p.member_id]?.handicap
      : p.guest_handicap;
    return courseHandicap(hcp, 130, 72, 72, 18);
  });
  return participants.map((p, idx) => {
    const key = p.member_id || p.guest_name || '';
    const gross = scores[key] || Array(18).fill(null);
    const ch = courseHcps[idx];
    const ph = scoring === 'net' && participants.length >= 2
      ? groupRelativePlayingHandicap(
        courseHcps.map((c) => ({ courseHcp: c })),
        { courseHcp: ch },
        formatId,
        handicapAllowance,
      )
      : playingHandicap(ch, formatId, handicapAllowance);
    const net = scoring === 'net'
      ? netScoresFromGross(gross, ph, strokeIndexes)
      : gross;
    return { id: key, gross, net };
  });
}

export function settleGame(
  formatId: string,
  participants: GameParticipant[],
  scores: HoleScores,
  memberMap: Record<string, MemberPublic>,
  courseHoles: CourseHole[],
  wagerCents: number,
  scoring: 'net' | 'gross' = 'net',
  handicapAllowance?: number | null,
) {
  const players = buildPlayers(participants, scores, memberMap, courseHoles, formatId, scoring, handicapAllowance);
  const betDollars = Math.round(wagerCents / 100) || 5;

  if (formatId === 'skins' || formatId.includes('skin')) {
    const result = computeSkins(players, scoring, betDollars);
    return { type: 'skins' as const, scoring, amount: betDollars, ...result };
  }

  if (players.length >= 2) {
    const result = computeNassau(players[0], players[1], scoring, betDollars);
    return { type: 'nassau' as const, scoring, amount: betDollars, ...result };
  }

  return null;
}
